import React, { useRef, useState, useEffect, useCallback } from "react";
import { Share2, Download, Link as LinkIcon, ImageDown, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const truncate = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s || "");
const STAGES = ["Prototype", "Alpha", "Beta", "Release Candidate", "Release"];
const stageIndex = (state = "") => {
  const k = (state || "").toLowerCase();
  if (k.startsWith("release candidate")) return 3;
  if (k.startsWith("release")) return 4;
  if (k.startsWith("beta")) return 2;
  if (k.startsWith("alpha")) return 1;
  return 0;
};
// Honest dev-state from a live % (mirrors the backend _state_for ladder) so the animation's
// stage label matches whatever depth value is currently on screen.
const stateForPct = (pct) => (pct >= 97 ? "Release" : pct >= 85 ? "Release Candidate" : pct >= 65 ? "Beta" : pct >= 40 ? "Alpha" : "Prototype");
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

// Shareable "Depth Journey" card — the game leveling up Prototype → Release. Every finished
// vision becomes an invite. Same canvas pattern as DareCard/ShareScoreCard.
// `journeyBefore` (the game's earliest recorded depth) makes it an animated before→after clip.
export default function DepthJourneyCard({ gameId, title, depth, genre, journeyBefore = 0 }) {
  const canvasRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const rafRef = useRef(null);

  const overall = depth?.overall ?? 0;
  const built = depth?.built ?? 0;
  const total = depth?.total ?? 0;
  const before = Math.max(0, Math.min(overall, Math.round(journeyBefore || 0)));
  const shareUrl = `${window.location.origin}/app/arcade/${gameId}`;
  const shortUrl = shareUrl.replace(/^https?:\/\//, "");

  // Draw one frame at a given live % — the number, the fill bar and the stage ladder all
  // reflect `val` so the clip animates the growth honestly.
  const draw = useCallback((val) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = 1200, H = 630;
    const state = stateForPct(val);

    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, "#050914"); grad.addColorStop(0.55, "#0a1830"); grad.addColorStop(1, "#0ea5e9");
    ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "rgba(4,7,15,0.62)"; ctx.fillRect(0, 0, W, H);

    ctx.fillStyle = "rgba(255,255,255,0.85)"; ctx.textBaseline = "top"; ctx.textAlign = "left";
    ctx.font = "bold 30px Inter, system-ui, sans-serif";
    ctx.fillText("🧭 GAMEVIEW · GAME COMPILER", 60, 48);

    ctx.fillStyle = "rgba(255,255,255,0.95)"; ctx.font = "bold 60px Inter, system-ui, sans-serif";
    ctx.fillText(truncate(title || "My Game", 30), 58, 110);
    if (genre) { ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.font = "28px Inter, system-ui, sans-serif"; ctx.fillText(truncate(genre, 46), 60, 186); }

    // Big depth number + a "from → now" hint while it climbs
    ctx.save();
    ctx.shadowColor = "#38bdf8"; ctx.shadowBlur = 26;
    ctx.fillStyle = "#7dd3fc"; ctx.font = "bold 180px Inter, system-ui, sans-serif";
    ctx.fillText(`${Math.round(val)}%`, 56, 250);
    ctx.restore();
    ctx.fillStyle = "rgba(255,255,255,0.9)"; ctx.font = "34px Inter, system-ui, sans-serif";
    ctx.fillText(`of the full vision built · ${built}/${total} systems`, 60, 452);
    if (before < overall) {
      ctx.fillStyle = "rgba(125,211,252,0.85)"; ctx.font = "bold 24px Inter, system-ui, sans-serif";
      ctx.textAlign = "right"; ctx.fillText(`grew ${before}% → ${overall}%`, 1140, 96); ctx.textAlign = "left";
    }

    // Stage ladder (fills proportionally to the live %)
    const frac = Math.max(0, Math.min(1, val / 100));
    const lx = 60, rx = 1140, ly = 545, n = STAGES.length;
    const cur = stageIndex(state);
    ctx.strokeStyle = "rgba(255,255,255,0.2)"; ctx.lineWidth = 6;
    ctx.beginPath(); ctx.moveTo(lx, ly); ctx.lineTo(rx, ly); ctx.stroke();
    const fillTo = lx + (rx - lx) * frac;
    const lg = ctx.createLinearGradient(lx, 0, rx, 0);
    lg.addColorStop(0, "#0ea5e9"); lg.addColorStop(1, "#a855f7");
    ctx.strokeStyle = lg; ctx.lineWidth = 6;
    ctx.beginPath(); ctx.moveTo(lx, ly); ctx.lineTo(fillTo, ly); ctx.stroke();
    for (let i = 0; i < n; i++) {
      const x = lx + (rx - lx) * (i / (n - 1));
      const done = i <= cur;
      ctx.beginPath(); ctx.arc(x, ly, i === cur ? 16 : 11, 0, Math.PI * 2);
      ctx.fillStyle = done ? (i === cur ? "#7dd3fc" : "#a855f7") : "rgba(255,255,255,0.28)"; ctx.fill();
      ctx.fillStyle = i === cur ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.55)";
      ctx.font = `${i === cur ? "bold " : ""}20px Inter, system-ui, sans-serif`;
      ctx.textAlign = i === 0 ? "left" : i === n - 1 ? "right" : "center";
      ctx.fillText(STAGES[i] === "Release Candidate" ? "RC" : STAGES[i], x, ly + 24);
    }
    ctx.textAlign = "left";
    ctx.fillStyle = "rgba(255,255,255,0.75)"; ctx.font = "24px Inter, system-ui, sans-serif";
    ctx.fillText(`▶ Play & remix it — ${truncate(shortUrl, 46)}`, 60, 596);
  }, [before, overall, built, total, title, genre, shortUrl]);

  const play = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const from = before, to = overall, dur = 2400;
    if (to <= from) { draw(to); return; }
    setPlaying(true);
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      draw(from + (to - from) * easeOutCubic(p));
      if (p < 1) { rafRef.current = requestAnimationFrame(tick); }
      else { rafRef.current = null; setPlaying(false); draw(to); }
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [before, overall, draw]);

  useEffect(() => {
    if (!open) { if (rafRef.current) cancelAnimationFrame(rafRef.current); return; }
    // slight delay so the canvas is mounted, then auto-play the growth clip
    const id = setTimeout(play, 120);
    return () => { clearTimeout(id); if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [open, play]);

  const toBlob = () => new Promise((res) => canvasRef.current.toBlob(res, "image/png"));

  const download = async () => {
    const blob = await toBlob(); if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `depth-journey-${overall}pct.png`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("Depth Journey card downloaded!");
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Share link copied — paste it anywhere!"); }
    catch { toast.error("Couldn't copy the link."); }
  };

  const nativeShare = async () => {
    const text = `"${title || "My game"}" is ${overall}% built (${stateForPct(overall)}) in the GameView Game Compiler — come play & remix it!`;
    try {
      const blob = await toBlob();
      const file = blob ? new File([blob], "gameview-depth-journey.png", { type: "image/png" }) : null;
      if (file && typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "GameView", text, url: shareUrl });
      } else {
        await navigator.share({ title: "GameView", text, url: shareUrl });
      }
    } catch { /* cancelled or unsupported */ }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full gap-1.5 h-7 px-3 text-xs" data-testid="compiler-journey-btn">
          <ImageDown className="w-3.5 h-3.5" /> Share journey
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="compiler-journey-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-[#0ea5e9]" /> Your game's Depth Journey</DialogTitle></DialogHeader>
        <canvas ref={canvasRef} width={1200} height={630} className="w-full rounded-xl border border-[hsl(var(--border))]" data-testid="compiler-journey-card" />
        <div className="flex flex-wrap gap-2">
          <Button onClick={play} disabled={playing} className="rounded-full gap-1.5 flex-1 bg-gradient-to-r from-[#0ea5e9] to-[#7c3aed]" data-testid="compiler-journey-play">
            <Play className="w-4 h-4" /> {playing ? "Playing…" : before < overall ? "Replay journey" : "Play"}
          </Button>
          <Button variant="outline" onClick={download} className="rounded-full gap-1.5 flex-1" data-testid="compiler-journey-download"><Download className="w-4 h-4" /> Image</Button>
          <Button variant="outline" onClick={copyLink} className="rounded-full gap-1.5 flex-1" data-testid="compiler-journey-copy"><LinkIcon className="w-4 h-4" /> Copy link</Button>
          {typeof navigator !== "undefined" && navigator.share && (
            <Button variant="outline" onClick={nativeShare} className="rounded-full gap-1.5 flex-1" data-testid="compiler-journey-share"><Share2 className="w-4 h-4" /> Share…</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
